import React, { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useHistory } from 'react-router';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { debounce, isEmpty } from 'lodash-es';
import {
  Heading4,
  Heading2,
  CenterBox,
  DoconInput,
  BaseBox,
  Icons,
  DoconInputWrapper,
} from '@docon/docon-ui-library';
import Layout, { MainBody } from 'components/Layout';
import HeaderBar from 'components/Layout/HeaderNavBar';
import DocItem from 'components/Doctors/DocItem';
import { searchDoctor } from 'redux/reducer/doctor';
import { Image, Loader } from 'shared';
import { borderColor, colors } from 'utilities/colorConfig';
import { cleverTapAnl } from 'utilities';
import { EventDoctorSearch, EventDoctorSelect } from 'utilities/clevertapEvent';

const Content = styled(BaseBox)`
  height: calc(100% - 100px);
  background: ${colors.white};
  overflow: auto;
  
  .zeroCase {
    height: inherit;
    place-content: center;
    text-align: center;
    padding: 12px;
    transform: translate(0, -8%);

    ${Image} { max-width: 100%; }
  }

  ${() => isMobileOnly && css`
    height: calc(100% - 50px);
  `}
`;

const Root = styled(Layout)`
  ${DoconInputWrapper} {
    background: ${colors.white};
  }

  ${DocItem} {
    border-bottom: 1px solid ${borderColor};
  }

  ${() => !isMobileOnly && css`
    ${MainBody} { padding-top: 0; }
  `}

  ${({ $isLoggedIn }) => !$isLoggedIn && css`
    ${MainBody} {
      max-width: 865px;
      padding-top: 16px;
      margin: auto;
    }
  `}
`;

function FallBack({
  src, heading, subHeading,
}: any) {
  return (
    <CenterBox className='zeroCase'>
      <Image src={src} mb='5%' />
      <Heading2 fontWeight={600} color={colors.tertiaryColor} title={heading} />
      <Heading4 color={colors.subHeading} title={subHeading} />
    </CenterBox>
  );
}

function DoctorSearch() {
  const {
    doctorSearchText,
    searchedDoctors,
    isFetchingSearchDoctor,
  } = useSelector(state => state.doctor);
  const { accessToken } = useSelector(state => state.auth);
  const [searchText, setSearchText] = useState(doctorSearchText);
  const dispatch = useDispatch();

  const history = useHistory();

  function handleChange({ value }: any) {
    setSearchText(value);
  }

  useEffect(() => {
    if (searchText !== doctorSearchText && searchText && searchText.length > 2)
      debouncedGetSearch(searchText);
  }, [searchText]);

  const debouncedGetSearch = useCallback(
    debounce((searchInput) => dispatch(searchDoctor(searchInput)), 300),
    [],
  );

  function handleClick({ shortUrl, doctorID, rank }: any) {
    triggerDoctorSearchEvent();

    cleverTapAnl.event({
      event_name: 'doctor_select',
      event: {
        page_name: 'doctor_search',
        is_search_active: true,
        search_term: searchText,
        doctor_id: doctorID,
        rank,
      },
    } as EventDoctorSelect);
    history.push(`/doctors/${shortUrl}`);
  }

  function handleBackClick() {
    triggerDoctorSearchEvent();
    history.goBack();
  }

  function renderContent() {
    if (isFetchingSearchDoctor) return <Loader />;

    if (searchText) {
      if (isEmpty(searchedDoctors))
        return (
          <FallBack
            src='/images/no_search_results.svg'
            heading='No results found'
            subHeading='Please check the spelling and search again'
          />
        );

      return (
        searchedDoctors.map((doctor: any, index: number) => (
          <DocItem
            key={doctor._id}
            rank={index + 1}
            doctor={doctor._source}
            onClick={handleClick}
          />
        ))
      );
    }

    return (
      <FallBack
        src='/images/empty_search.svg'
        heading='Search for your favourite doctor'
      />
    );
  }

  function triggerDoctorSearchEvent() {
    cleverTapAnl.event({
      event_name: 'doctor_search',
      event: {
        page_name: 'doctor_search',
        search_term: searchText,
        search_term_char_count: (searchText || '').length,
        search_result_count: searchText ? searchedDoctors.length : 0,
        search_results_flag: searchText ? !!searchedDoctors.length : false,
      },
    } as EventDoctorSearch);
  }

  return (
    <Root
      $isLoggedIn={accessToken}
      hideHeader={!accessToken}
      hideFooter={isMobileOnly || !accessToken}
      header={isMobileOnly ? <HeaderBar onBackIconClick={handleBackClick} title={'Doctor Search'} /> : null}
    >
      <HeaderBar onBackIconClick={handleBackClick} hidden={isMobileOnly || !accessToken} title={'Doctor Search'} />

      <DoconInput
        autoFocus
        name='search'
        value={searchText}
        placeholder='Search and book your doctor'
        prefix={<Icons.SearchIcon /> as any}
        onChange={handleChange}
      />

      <Content>
        {renderContent()}
      </Content>
    </Root >
  );
}

export default DoctorSearch;